/**
 * Quick task 260611-gre — Linear-inspired list view for the KanbanBoardPage.
 *
 * Renders tasks grouped by milestone as collapsible top-level sections (matching
 * the board's swimlane ordering). Within each milestone, state sub-headers
 * (non-collapsible, lighter visual weight) separate the tasks by column state in
 * board column order. A leading Triage section (internal only) renders before all
 * milestone groups as a flat list.
 *
 * Phase 23 isolation: FORBIDDEN to import useAuth / useAuthOptional here.
 * `audience` flows in as a prop from KanbanBoardPage. To avoid a conditional
 * hook call (rule-of-hooks violation) the component is split into two
 * sub-components:
 *   - InternalListSections: calls useMilestones(projectId, 'internal') —
 *     internal client.ts transport — renders full row + state sub-headers
 *     inside milestone groups
 *   - ExternalListSections: calls useMilestones(projectId, 'external') —
 *     raw-fetch transport (services/api/externalMilestones.ts), same
 *     structure without auth imports
 * The parent picks one at render time based on `audience`.
 *
 * external-board-redirect-login (2026-07-02) CORRECTION: the endpoint being
 * authenticateAny-gated (Phase 51 commit b10ab690) does NOT make the internal
 * client.ts transport safe for external callers. Any 401 through client.ts
 * (expired session, revoked invite, transient hiccup, …) clears the internal
 * auth token AND fires the global `auth:expired` navigation, redirecting the
 * external viewer to the INTERNAL /login page — that is the exact bug this
 * session fixed. ExternalListSections MUST keep using the 'external' audience
 * (raw fetch, no client.ts) regardless of what the backend accepts.
 */
import { useState, useMemo, useCallback } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { TaskCard, BoardColumn } from '../../services/api/board';
import { TaskStatusPicker } from './TaskStatusPicker';
import { Avatar } from '../ui/Avatar';
import { useMilestones } from '../../hooks/useMilestones';
import type { Milestone } from '../../services/api/milestones';
import { formatDateRange } from './milestoneCounts';
import { useInternalChipVisibility } from './internalChipVisibility';
import {
  getTierColor,
  chipTextColor,
  formatDisplayDate,
  taskStalenessEmoji,
  DISABLED_TASK_STALENESS_CONFIG,
  type TaskStalenessConfig,
} from '../../utils/format';

export interface ProjectListViewProps {
  projectId: string;
  projectIdNum: number;
  audience: 'internal' | 'external';
  /**
   * Non-triage columns, post-filter-bar (filteredData.columns) — this is the
   * SAME filtered data the board renders, so every BoardFilterBar slot
   * (assignee, status, milestone, dates, priority, and MKS-164's labels)
   * already applies here for free; this view never re-derives its own
   * filter.
   */
  columns: BoardColumn[];
  /** Synthetic Triage column or null. Only rendered when audience === 'internal'. */
  triageColumn: BoardColumn | null;
  onRowClick: (task: TaskCard) => void;
  onStateChangeError?: (message: string) => void;
  /**
   * 260913-jpy — per-project staleness indicator config, forwarded to the
   * internal row renderer only (external audience never sees the emoji).
   * Omitted means the disabled default at the render call site.
   */
  stalenessConfig?: TaskStalenessConfig;
}

// Sentinel key used for the triage section in the collapsed set.
const TRIAGE_SECTION_KEY = -1;

// ---------------------------------------------------------------------------
// Shared low-level row sub-components
// ---------------------------------------------------------------------------

interface StateDotProps {
  color: string;
}

function StateDot({ color }: StateDotProps) {
  return (
    <span
      aria-hidden
      className="w-2 h-2 rounded-full shrink-0"
      style={{ background: color }}
    />
  );
}

// ---------------------------------------------------------------------------
// Internal/External visibility badge (read-only; no flip button in list view)
// Mirror of TaskCard.tsx renderIndicator — no flip affordance.
// ---------------------------------------------------------------------------
interface VisibilityBadgeProps {
  isExternal: boolean;
  /** MKS-534 — the card's own project, for the "no external collaborators" rule. */
  projectId: number;
}

function VisibilityBadge({ isExternal, projectId }: VisibilityBadgeProps) {
  // MKS-534 — same rule as TaskCard's chip: a project with nobody external on
  // it says "Internal" on every row, which is noise. An external row keeps its
  // amber badge, and an unknown roster keeps today's badge.
  const { isInternalChipHidden } = useInternalChipVisibility();
  if (!isExternal && isInternalChipHidden(projectId)) return null;

  const chipColor = isExternal ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700';
  const dotColor = isExternal ? 'bg-amber-500' : 'bg-green-500';
  const label = isExternal ? 'External' : 'Internal';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium shrink-0 ${chipColor}`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} aria-hidden />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Milestone section header — top-level collapsible (like board swimlane header)
// ---------------------------------------------------------------------------
interface MilestoneSectionHeaderProps {
  name: string;
  dateRange: string | null;
  taskCount: number;
  isExpanded: boolean;
  onToggle: () => void;
}

function MilestoneSectionHeader({
  name,
  dateRange,
  taskCount,
  isExpanded,
  onToggle,
}: MilestoneSectionHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-ms-canvas border-b border-ms-border-subtle text-left"
    >
      <ChevronRightIcon
        aria-hidden
        className={`w-3 h-3 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
      />
      <span className="text-sm font-semibold text-ms-text-strong truncate flex-1">{name}</span>
      {dateRange && (
        <span className="text-xs text-ms-text-muted shrink-0">{dateRange}</span>
      )}
      <span className="text-xs text-ms-text-muted shrink-0">{taskCount}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// State sub-header row (lighter, non-collapsible; sits inside a milestone group)
// ---------------------------------------------------------------------------
interface StateSubHeaderProps {
  name: string;
  color: string;
  taskCount: number;
}

function StateSubHeader({ name, color, taskCount }: StateSubHeaderProps) {
  return (
    <div className="flex items-center gap-2 pl-6 pr-3 py-1 bg-ms-canvas border-b border-ms-border-subtle">
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
      <span className="text-xs font-medium text-ms-text-secondary truncate flex-1">{name}</span>
      <span className="text-xs text-ms-text-muted shrink-0">{taskCount}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Triage section header (no milestone grouping — flat, collapsible like before)
// ---------------------------------------------------------------------------
interface SectionHeaderProps {
  name: string;
  color: string;
  taskCount: number;
  isExpanded: boolean;
  onToggle: () => void;
}

function SectionHeader({ name, color, taskCount, isExpanded, onToggle }: SectionHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-ms-canvas border-b border-ms-border-subtle text-left"
    >
      <ChevronRightIcon
        aria-hidden
        className={`w-3 h-3 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
      />
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
      <span className="text-sm font-semibold text-ms-text-strong">{name}</span>
      <span className="text-xs text-ms-text-muted">{taskCount}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Milestone grouping data structure
// ---------------------------------------------------------------------------

interface MilestoneGroup {
  /** null = "No milestone" sentinel */
  milestone: Milestone | null;
  /**
   * Tasks per column, in board column order.
   * Only entries with at least one task are included.
   */
  tasksByColumn: Array<{ column: BoardColumn; tasks: TaskCard[] }>;
  totalTasks: number;
}

/**
 * Build milestone groups from all columns, preserving:
 *  - Milestone order: server order from useMilestones (DESC start_date, same as
 *    KanbanSwimlane swimlane ordering). Only milestones that have at least one
 *    task across any column are included.
 *  - "No milestone" appended last, only when there are tasks with milestoneId === null.
 *  - Within each group, columns appear in their original board ordering (columns
 *    prop order), all states included (even 0-count), matching Linear's sub-group style.
 */
function buildMilestoneGroups(columns: BoardColumn[], milestones: Milestone[]): MilestoneGroup[] {
  const groups: MilestoneGroup[] = [];

  for (const ms of milestones) {
    const tasksByColumn = columns.map((col) => ({
      column: col,
      tasks: col.tasks.filter((t) => t.milestoneId === ms.id),
    }));
    const totalTasks = tasksByColumn.reduce((sum, e) => sum + e.tasks.length, 0);
    if (totalTasks === 0) continue;
    groups.push({ milestone: ms, tasksByColumn, totalTasks });
  }

  // "No milestone" — trailing
  const noMilestoneByColumn = columns.map((col) => ({
    column: col,
    tasks: col.tasks.filter((t) => t.milestoneId === null),
  }));
  const noMilestoneTotal = noMilestoneByColumn.reduce((sum, e) => sum + e.tasks.length, 0);
  if (noMilestoneTotal > 0) {
    groups.push({ milestone: null, tasksByColumn: noMilestoneByColumn, totalTasks: noMilestoneTotal });
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Internal-audience list sections (calls useMilestones hook)
// ---------------------------------------------------------------------------
interface InternalListSectionsProps {
  projectId: string;
  projectIdNum: number;
  columns: BoardColumn[];
  triageColumn: BoardColumn | null;
  onRowClick: (task: TaskCard) => void;
  onStateChangeError?: (message: string) => void;
  /** 260913-jpy — forwarded to renderRow. */
  stalenessConfig?: TaskStalenessConfig;
}

function InternalListSections({
  projectId,
  projectIdNum,
  columns,
  triageColumn,
  onRowClick,
  onStateChangeError,
  stalenessConfig,
}: InternalListSectionsProps) {
  const milestonesQuery = useMilestones(projectId, 'internal');

  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // All columns (triage + real) for the TaskStatusPicker so it has the full set.
  // TaskStatusPicker internally filters out isTriage, so passing it is safe.
  const allColumnsForPicker = useMemo(
    () => [...(triageColumn ? [triageColumn] : []), ...columns],
    [triageColumn, columns],
  );

  const milestoneGroups = useMemo(
    () => buildMilestoneGroups(columns, milestonesQuery.data ?? []),
    [columns, milestonesQuery.data],
  );

  function renderRow(task: TaskCard, column: BoardColumn) {
    const isTriageRow = task.taskStateId === null;

    return (
      <div
        key={task.id}
        role="button"
        tabIndex={0}
        data-testid={`list-row-${task.id}`}
        onClick={() => onRowClick(task)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick(task);
          }
        }}
        className="flex items-center gap-2 px-3 py-1.5 hover:bg-ms-canvas border-b border-ms-border-subtle cursor-pointer"
      >
        {/* Task code */}
        <span className="text-[11px] font-mono uppercase text-ms-text-muted shrink-0 w-16 truncate">
          {task.code ?? ''}
        </span>

        {/* State dot — interactive picker for real states; inert dot for triage */}
        {!isTriageRow ? (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TaskStatusPicker
              taskId={task.id}
              projectId={projectIdNum}
              currentStateId={task.taskStateId}
              columns={allColumnsForPicker}
              canManage={true}
              audience="internal"
              onError={onStateChangeError}
              /* MKS-170 — the chip sits ~80px from the left edge here, so the
                 default right-anchoring pushed the 224px panel off-container
                 and clipped every state name from the left. */
              menuAlign="left"
            />
          </div>
        ) : (
          <StateDot color={column.color} />
        )}

        {/* Title */}
        <span className="flex-1 min-w-0 text-sm text-ms-text-strong truncate">{task.name}</span>

        {/* Right metadata cluster */}
        <VisibilityBadge isExternal={task.isExternal} projectId={task.projectId} />
        {task.tier && (
          <span
            style={{
              backgroundColor: getTierColor(task.tier.orderIndex),
              color: chipTextColor(getTierColor(task.tier.orderIndex)),
            }}
            className="rounded-full px-1.5 py-0.5 text-[10px] font-bold shrink-0"
          >
            P{task.tier.orderIndex}
          </span>
        )}
        {task.assigneeName && (
          <Avatar
            name={task.assigneeName}
            colorSeq={task.assigneeColorSeq}
            avatarUrl={task.assigneeAvatarUrl}
            size="xs"
          />
        )}
        {/* Phase 66 (EAP-07) — amber "External" pill for externally-assigned tasks. */}
        {task.assigneeName && task.assigneeKind === 'external_user' && (
          <span className="text-[9px] font-semibold px-1 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">
            External
          </span>
        )}
        {/* 260723 — internal-only staleness emoji (💩 >5d, 🪦 >15d with no
            update). This row renders only for the internal audience, and it is
            suppressed for terminal columns (Done / Archived / Cancelled). */}
        {(() => {
          // 260913-jpy — substitute the disabled default when this surface
          // was never wired to a real per-project config, so an unwired
          // call site shows nothing rather than falling back to the
          // always-on global ladder.
          const staleEmoji = taskStalenessEmoji(task.lastActivityAt, {
            isTerminal: column.isTerminal,
            config: stalenessConfig ?? DISABLED_TASK_STALENESS_CONFIG,
          });
          return staleEmoji ? (
            <span
              role="img"
              aria-label={staleEmoji.label}
              title={staleEmoji.label}
              className="text-sm leading-none shrink-0"
            >
              {staleEmoji.emoji}
            </span>
          ) : null;
        })()}
        <span className="text-xs text-ms-text-muted shrink-0 w-20 text-right">
          {formatDisplayDate(task.createdAt)}
        </span>
      </div>
    );
  }

  return (
    <>
      {/* Triage section — internal only, leading flat list (no milestone sub-grouping) */}
      {triageColumn && (
        <div data-testid={`list-section-${TRIAGE_SECTION_KEY}`}>
          <SectionHeader
            name={triageColumn.name}
            color={triageColumn.color}
            taskCount={triageColumn.tasks.length}
            isExpanded={!collapsed.has(String(TRIAGE_SECTION_KEY))}
            onToggle={() => toggle(String(TRIAGE_SECTION_KEY))}
          />
          {!collapsed.has(String(TRIAGE_SECTION_KEY)) &&
            triageColumn.tasks.map((task) => renderRow(task, triageColumn))}
        </div>
      )}

      {/* Milestone groups — top-level collapsible sections in swimlane order */}
      {milestoneGroups.map((group) => {
        const msKey = group.milestone ? `ms-${group.milestone.id}` : 'ms-none';
        const name = group.milestone ? group.milestone.name : 'No milestone';
        const dateRange = group.milestone
          ? formatDateRange(group.milestone.startDate, group.milestone.endDate)
          : null;
        const isExpanded = !collapsed.has(msKey);

        return (
          <div key={msKey} data-testid={`list-milestone-${group.milestone?.id ?? 'none'}`}>
            <MilestoneSectionHeader
              name={name}
              dateRange={dateRange}
              taskCount={group.totalTasks}
              isExpanded={isExpanded}
              onToggle={() => toggle(msKey)}
            />
            {isExpanded &&
              group.tasksByColumn.map(({ column, tasks }) => {
                if (tasks.length === 0) return null;
                return (
                  <div key={column.taskStateId ?? 0}>
                    <StateSubHeader
                      name={column.name}
                      color={column.color}
                      taskCount={tasks.length}
                    />
                    {tasks.map((task) => renderRow(task, column))}
                  </div>
                );
              })}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// External-audience list sections (milestone top-level groups, no state picker)
//
// external-board-redirect-login (2026-07-02) — Phase 51 (commit b10ab690) made
// GET /projects/:id/milestones accept external session cookies via
// authenticateAny, but that is a BACKEND authorization fact, not a frontend
// transport safety guarantee. The previous version of this comment claimed
// "no isolation breach" because useMilestones called apiGet (client.ts) with
// credentials:'include' — that reasoning missed that client.ts's 401 handler
// unconditionally clears the internal auth token and fires the global
// `auth:expired` navigation on ANY 401 (expired session, revoked invite,
// transient hiccup, …), redirecting the external viewer to the INTERNAL
// /login page regardless of why the 401 happened. useMilestones now takes an
// explicit audience and routes 'external' through the raw-fetch
// services/api/externalMilestones.ts transport (Landmine #8) — see
// useMilestones.ts header comment for the full trace.
// ---------------------------------------------------------------------------
interface ExternalListSectionsProps {
  projectId: string;
  columns: BoardColumn[];
  onRowClick: (task: TaskCard) => void;
}

function ExternalListSections({ projectId, columns, onRowClick }: ExternalListSectionsProps) {
  const milestonesQuery = useMilestones(projectId, 'external');

  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const milestoneGroups = useMemo(
    () => buildMilestoneGroups(columns, milestonesQuery.data ?? []),
    [columns, milestonesQuery.data],
  );

  function renderRow(task: TaskCard, column: BoardColumn) {
    return (
      <div
        key={task.id}
        role="button"
        tabIndex={0}
        data-testid={`list-row-${task.id}`}
        onClick={() => onRowClick(task)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick(task);
          }
        }}
        className="flex items-center gap-2 px-3 py-1.5 hover:bg-ms-canvas border-b border-ms-border-subtle cursor-pointer"
      >
        {/* Task code */}
        <span className="text-[11px] font-mono uppercase text-ms-text-muted shrink-0 w-16 truncate">
          {task.code ?? ''}
        </span>

        {/* State dot — inert on external surface */}
        <StateDot color={column.color} />

        {/* Title */}
        <span className="flex-1 min-w-0 text-sm text-ms-text-strong truncate">{task.name}</span>

        {/* Right metadata cluster — no visibility badge on external */}
        {task.tier && (
          <span
            style={{
              backgroundColor: getTierColor(task.tier.orderIndex),
              color: chipTextColor(getTierColor(task.tier.orderIndex)),
            }}
            className="rounded-full px-1.5 py-0.5 text-[10px] font-bold shrink-0"
          >
            P{task.tier.orderIndex}
          </span>
        )}
        {task.assigneeName && (
          <Avatar
            name={task.assigneeName}
            colorSeq={task.assigneeColorSeq}
            avatarUrl={task.assigneeAvatarUrl}
            size="xs"
          />
        )}
        {/* Phase 66 (EAP-07) — amber "External" pill for externally-assigned tasks. */}
        {task.assigneeName && task.assigneeKind === 'external_user' && (
          <span className="text-[9px] font-semibold px-1 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">
            External
          </span>
        )}
        <span className="text-xs text-ms-text-muted shrink-0 w-20 text-right">
          {formatDisplayDate(task.createdAt)}
        </span>
      </div>
    );
  }

  return (
    <>
      {/* Milestone groups — top-level collapsible sections in swimlane order */}
      {milestoneGroups.map((group) => {
        const msKey = group.milestone ? `ms-${group.milestone.id}` : 'ms-none';
        const name = group.milestone ? group.milestone.name : 'No milestone';
        const dateRange = group.milestone
          ? formatDateRange(group.milestone.startDate, group.milestone.endDate)
          : null;
        const isExpanded = !collapsed.has(msKey);

        return (
          <div key={msKey} data-testid={`list-milestone-${group.milestone?.id ?? 'none'}`}>
            <MilestoneSectionHeader
              name={name}
              dateRange={dateRange}
              taskCount={group.totalTasks}
              isExpanded={isExpanded}
              onToggle={() => toggle(msKey)}
            />
            {isExpanded &&
              group.tasksByColumn.map(({ column, tasks }) => {
                if (tasks.length === 0) return null;
                return (
                  <div key={column.taskStateId ?? 0}>
                    <StateSubHeader
                      name={column.name}
                      color={column.color}
                      taskCount={tasks.length}
                    />
                    {tasks.map((task) => renderRow(task, column))}
                  </div>
                );
              })}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Public component — picks the right sub-component by audience
// ---------------------------------------------------------------------------

export function ProjectListView({
  projectId,
  projectIdNum,
  audience,
  columns,
  triageColumn,
  onRowClick,
  onStateChangeError,
  stalenessConfig,
}: ProjectListViewProps) {
  return (
    <div
      data-testid="project-list-view"
      className="rounded-lg border border-ms-border bg-ms-surface overflow-hidden"
    >
      {audience === 'internal' ? (
        <InternalListSections
          projectId={projectId}
          projectIdNum={projectIdNum}
          columns={columns}
          triageColumn={triageColumn}
          onRowClick={onRowClick}
          onStateChangeError={onStateChangeError}
          stalenessConfig={stalenessConfig}
        />
      ) : (
        <ExternalListSections projectId={projectId} columns={columns} onRowClick={onRowClick} />
      )}
    </div>
  );
}
